import { Card, CardContent } from '@/components/ui/card';
import { Shield } from 'lucide-react';

interface CleanClaimScorecardProps {
  probability: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
}

export function CleanClaimScorecard({ probability, riskLevel }: CleanClaimScorecardProps) {
  const pct = riskLevel === 'LOW' ? Math.max(probability, 96) : riskLevel === 'HIGH' ? Math.min(probability, 68) : probability;

  const isGood = pct >= 90;
  const isWarn = pct >= 75 && pct < 90;

  const ringTrack = isGood ? 'stroke-primary/20' : isWarn ? 'stroke-amber-100' : 'stroke-red-100';
  const ringStroke = isGood ? 'stroke-primary' : isWarn ? 'stroke-amber-500' : 'stroke-red-500';
  const ringColor = isGood ? 'text-primary' : isWarn ? 'text-amber-500' : 'text-destructive';

  const label = isGood ? 'Excellent' : isWarn ? 'Needs Review' : 'At Risk';
  const desc = isGood
    ? 'All documentation criteria met for first-pass adjudication.'
    : 'Review warnings before finalizing to improve claim acceptance.';

  return (
    <Card className="border-border/60">
      <CardContent className="p-5 space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" /> First-Pass Clean Claim Probability
        </h4>
        <div className="flex items-center gap-5">
          <div className="relative w-24 h-24 shrink-0">
            <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="42" fill="none" className={ringTrack} strokeWidth="8" />
              <circle
                cx="50" cy="50" r="42" fill="none"
                className={ringStroke}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${pct * 2.64} ${264 - pct * 2.64}`}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-2xl font-bold ${ringColor}`}>{pct}%</span>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-foreground">{label}</div>
            <p className="text-xs text-muted-foreground mt-1">{desc}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
